import { Component, h } from 'preact';
import produce from 'immer';
import { AmPaxAmount, AmRooms, MAX_AM_PAX_AMOUNT } from '../../../../common';
import { Column } from '../../../../common/Layout/Layout';
import { ModalProps } from '../../../../modal';
import { Actions, actions, BookingState, connectStore } from '../../../../store';
import { withStyles, WithStyles } from '../../../../style';
import { PaxAmountModalBase } from '../PaxAmountModalBase';
import RoomSelection from '../RoomSelection/RoomSelection';
import Icon from '../../../../common/Icon/Icon';
import { calculateTotalAndRemainingAmPaxAmount } from '../../paxAmountUtils';
import { FinText } from '../../../../i18n';

export interface AmPaxAmountModalProps extends ModalProps {}

interface StoreProps {
  rooms: AmRooms;
}

type EffectiveProps = AmPaxAmountModalProps & StoreProps & Actions & WithStyles;

export interface AmPaxAmountModalState {
  rooms: AmRooms;
}

const mapStateToProps = ({
  amState: {
    amSelection: { rooms },
  },
}: BookingState): StoreProps => ({ rooms });

@withStyles()
@connectStore({ mapStateToProps, actions })
export default class AmPaxAmountModal extends Component<AmPaxAmountModalProps, AmPaxAmountModalState> {
  constructor(props: EffectiveProps) {
    super(props);
    this.state = { rooms: props.rooms };
  }

  updateRoomPaxAmount = (amPaxAmount: AmPaxAmount): void => {
    this.setState((prevState) =>
      produce(prevState, ({ rooms }: AmPaxAmountModalState) => {
        rooms[amPaxAmount.roomIndex] = amPaxAmount;
      })
    );
  };

  getEmptyRoom = (): AmPaxAmount => ({
    adults: 1,
    children: 0,
    childrenAges: [],
    roomIndex: this.state.rooms.length,
  });

  addRoom = (): void => {
    this.setState((prevState) =>
      produce(prevState, ({ rooms }: AmPaxAmountModalState) => {
        const newRoom: AmPaxAmount = this.getEmptyRoom();

        (this.props as EffectiveProps).logGtmUiEvent({ id: 'addRoom', state: `room-${newRoom.roomIndex}` });
        rooms.push(newRoom);
      })
    );
  };

  removeRoom = (roomIndex: number): void => {
    this.setState((prevState) =>
      produce(prevState, ({ rooms }: AmPaxAmountModalState) => {
        (this.props as EffectiveProps).logGtmUiEvent({ id: 'removeRoom', state: `room-${roomIndex}` });
        rooms.splice(roomIndex, 1);
        rooms.forEach((room, index) => (room.roomIndex = index));
      })
    );
  };

  updateRooms = (): void => {
    (this.props as EffectiveProps).setAmRooms(this.state.rooms);
  };

  render({ tx }: EffectiveProps): h.JSX.Element {
    const { maxTotalRooms, maxTotalPax } = MAX_AM_PAX_AMOUNT;
    const { remaining, total } = calculateTotalAndRemainingAmPaxAmount(this.state.rooms);
    const canAddRoom: boolean = this.state.rooms.length < maxTotalRooms && total.adults + total.children < maxTotalPax;

    return (
      <PaxAmountModalBase onClose={this.updateRooms}>
        <Column grow={false} class={tx('ps-large-x')}>
          {this.state.rooms.map((room: AmPaxAmount) => (
            <RoomSelection
              key={room.roomIndex}
              maxRemainingPaxes={remaining}
              paxAmount={room}
              onUpdate={this.updateRoomPaxAmount}
              onRemove={this.removeRoom}
              canRemove={room.roomIndex !== 0}
            />
          ))}
          {canAddRoom && (
            <button
              class={tx('flex flex--middle mr-small-y nordic-blue-900-text font-body-1 bold-type')}
              onClick={this.addRoom}
              data-test-add-room
            >
              <span class={tx('ms-xsmall-r')}>
                <Icon iconName="plus-outline" class={tx('is-small')} />
              </span>
              <FinText id="aurinkomatkat.paxAmount.addRoom" />
            </button>
          )}
        </Column>
      </PaxAmountModalBase>
    );
  }
}
